import {
  Button,
  ConnectionGroup,
  PageGridCell,
  PageGridColumn,
  PageGridRow,
  StatusBanner,
  type MultiConnectionGroupItem,
} from "@wayflyer/flyui";
import { IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  ACCOUNTING,
  MARKETING,
  CODAT_SUPPORTED_ACCOUNTING,
  isPrimaryRevenuePlatform,
  isSecondaryRevenuePlatform,
  SHOPPING,
  filteredAndSortedDataSourcesNext,
  dataSourceSlugToName,
} from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";
import type { FundingApplicationSchemas } from "api-types";

import {
  getChecklistItemForUnsupportedPlatform,
  getChecklistItemPropsForConnector,
} from "../../../utils/checklist-items";
import { filterConnectorsByStageConfig, getFilteredAccountingConnectors } from "../../../utils/connectors";
import { NOT_LISTED } from "../../../utils/funding-journey";
import { MESSAGES } from "../message-definitions";
import { AddCustomPlatformInput } from "../../../components";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import {
  useFundingApplication,
  useFundingApplicationMutation,
  type FormattedFundingApplicationScreenUpdate,
} from "../../../queries/funding-application/queries";
import { usePlatformConnectors } from "../../../queries/connectors/queries";

import { validateSelection } from "./platform-selection-validation";
import { SkipPlatformSelectionModal } from "./skip-platform-selection-modal";

type UnsupportedPlatformUiState = {
  name: string;
  checked: boolean;
};

const stripUncheckedPlatforms = (selectedConnectors: { [x: string]: boolean } | undefined): string[] => {
  return selectedConnectors
    ? Object.entries(selectedConnectors)
        .filter(([, value]) => value)
        .map(([key]) => key)
    : [];
};

interface SelectPlatformsNextProps {
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
}

export const SelectPlatformsNext = ({ fundingApplicationStages }: SelectPlatformsNextProps) => {
  const intl = useIntl();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);

  const areAccountingDocumentsRequired =
    fundingApplicationMutation.variables?.accountingDocumentsRequired ?? fundingApplication.accountingDocumentsRequired;

  const { data: connectors } = usePlatformConnectors(customerDomain, { skipConnections: true }); // we don't need the connections here
  const filteredandSortedDataSources = filteredAndSortedDataSourcesNext(connectors);
  const [errors, setErrors] = useState<string[]>([]);

  const showRevenue = fundingApplicationStages.revenue?.allowPlatformSelection;
  const showMarketing = fundingApplicationStages.marketing?.allowPlatformSelection;
  const showAccounting = fundingApplicationStages.accounting?.allowPlatformSelection;

  const [selectedPlatforms, setSelectedPlatforms] = useState<Record<string, boolean>>(
    (fundingApplication?.selectedConnectorSlugs || []).reduce((acc, slug) => ({ ...acc, [slug]: true }), {})
  );

  const [showSecondaryDeselectedMessage, setShowSecondaryDeselectedMessage] = useState(false);
  const [isSkipRevenuePlatformSelected, setIsSkipRevenuePlatformSelected] = useState(
    fundingApplication?.isEcommerceUnsupported ?? false
  );

  const {
    hasRevenuePrimarySelected,
    selectedRevenuePrimaryPlatformName,
    hasRevenueSecondarySelected,
    hasRequiredRevenueSlug,
  } = useMemo(() => {
    const selectedSlugs = Object.entries(selectedPlatforms)
      .filter(([, isSelected]) => isSelected)
      .map(([slug]) => slug);

    const primaryRevenuePlatform = selectedSlugs.find((slug) => isPrimaryRevenuePlatform(slug));
    const hasRevenueSecondarySelected = selectedSlugs.some((slug) => isSecondaryRevenuePlatform(slug));
    const hasRequiredRevenueSlug = !!fundingApplicationStages.revenue?.requiredConnectorSlugs?.length;

    return {
      hasRevenuePrimarySelected: !!primaryRevenuePlatform,
      selectedRevenuePrimaryPlatformName: primaryRevenuePlatform
        ? dataSourceSlugToName(primaryRevenuePlatform)
        : undefined,
      hasRevenueSecondarySelected,
      hasRequiredRevenueSlug,
    };
  }, [selectedPlatforms, fundingApplicationStages.revenue?.requiredConnectorSlugs]);

  const handlePlatformToggle = ({ slug, value }: { slug: string; value: boolean }) => {
    const previousState = selectedPlatforms;
    let newState = {
      ...selectedPlatforms,
      [slug]: value,
    };

    // only shopify2 or shopify-appstore can be present, if adding one, ensure the other is removed
    if (slug === "shopify2" && value) {
      newState = {
        ...newState,
        ["shopify-appstore"]: false,
      };
    } else if (slug === "shopify-appstore" && value) {
      newState = {
        ...newState,
        ["shopify2"]: false,
      };
    }

    const hadRevenueSecondaryBefore = Object.entries(previousState).some(
      ([key, isSelected]) => isSelected && isSecondaryRevenuePlatform(key)
    );

    // if a primary revenue platform is selected, deselect all secondary platforms
    if (isPrimaryRevenuePlatform(slug) && value) {
      Object.keys(newState).forEach((key) => {
        if (isSecondaryRevenuePlatform(key)) {
          newState[key] = false;
        }
      });
      setShowSecondaryDeselectedMessage(hadRevenueSecondaryBefore);
    } else {
      setShowSecondaryDeselectedMessage(false);
    }

    // Optimistically update local state immediately
    setSelectedPlatforms(newState);

    const isOnlineSalesPlatform = isPrimaryRevenuePlatform(slug) || isSecondaryRevenuePlatform(slug);
    const shouldUncheckSkip = value && isSkipRevenuePlatformSelected && isOnlineSalesPlatform;
    if (shouldUncheckSkip) {
      setIsSkipRevenuePlatformSelected(false);
    }

    fundingApplicationMutation.mutate(
      {
        id: fundingApplication.id,
        selectedConnectorSlugs: stripUncheckedPlatforms(newState),
        ...(shouldUncheckSkip ? { isEcommerceUnsupported: false } : {}),
      },
      {
        onError: () => {
          // Rollback on error
          setSelectedPlatforms(previousState);
          setShowSecondaryDeselectedMessage(false);
          if (shouldUncheckSkip) {
            setIsSkipRevenuePlatformSelected(true);
          }
        },
      }
    );
  };

  const [selectedUnsupportedPlatforms, setSelectedUnsupportedPlatforms] = useState<
    Record<string, Array<UnsupportedPlatformUiState>>
  >({
    [SHOPPING]: (fundingApplication?.selectedUnsupportedEcommerce || []).map((name) => ({ name, checked: true })),
    [MARKETING]: (fundingApplication?.selectedUnsupportedMarketingAnalytics || []).map((name) => ({
      name,
      checked: true,
    })),
    [ACCOUNTING]: (fundingApplication?.selectedUnsupportedAccounting.filter((slug) => slug !== NOT_LISTED) || []).map(
      (name) => ({ name, checked: true })
    ),
  });

  const handleUnsupportedPlatformToggle = ({
    category,
    name,
    value,
  }: {
    category: string;
    name: string;
    value: boolean;
  }) => {
    const previousState = selectedUnsupportedPlatforms;
    const previousPlatforms = selectedPlatforms;

    const newState = {
      ...selectedUnsupportedPlatforms,
      [category]: [
        ...selectedUnsupportedPlatforms[category].filter((platform) => platform.name !== name),
        { name, checked: value },
      ]
        .filter((platform) => platform.name !== "")
        .filter((platform) => !(platform.name === NOT_LISTED && category === ACCOUNTING)),
    };

    // Optimistically update local state immediately
    setSelectedUnsupportedPlatforms(newState);

    const updates: FormattedFundingApplicationScreenUpdate = {
      id: fundingApplication.id,
      selectedUnsupportedEcommerce: newState[SHOPPING].filter(({ checked }) => checked).map(({ name }) => name),
      selectedUnsupportedMarketingAnalytics: newState[MARKETING].filter(({ checked }) => checked).map(
        ({ name }) => name
      ),
      selectedUnsupportedAccounting: newState[ACCOUNTING].filter(({ checked }) => checked).map(({ name }) => name),
    };

    if (category === ACCOUNTING && name === NOT_LISTED) {
      updates.accountingDocumentsRequired = value;
    }

    // if a known codat accounting platfrom is being added ensure generic codat connector in list of selected slugs
    if (newState[ACCOUNTING].some(({ name }) => CODAT_SUPPORTED_ACCOUNTING.includes(name))) {
      if (!fundingApplication.selectedConnectorSlugs.includes("codat_accounting")) {
        updates.selectedConnectorSlugs = [...fundingApplication.selectedConnectorSlugs, "codat_accounting"];
        // Also optimistically update selectedPlatforms
        setSelectedPlatforms((prev) => ({ ...prev, codat_accounting: true }));
      }
    } else {
      // make sure it's not in the list
      if (fundingApplication.selectedConnectorSlugs.includes("codat_accounting")) {
        updates.selectedConnectorSlugs = fundingApplication.selectedConnectorSlugs.filter(
          (slug) => slug !== "codat_accounting"
        );
        // Also optimistically update selectedPlatforms
        setSelectedPlatforms((prev) => ({ ...prev, codat_accounting: false }));
      }
    }

    fundingApplicationMutation.mutate(updates, {
      onError: () => {
        // Rollback on error
        setSelectedUnsupportedPlatforms(previousState);
        setSelectedPlatforms(previousPlatforms);
      },
    });
  };

  const handleSkipRevenuePlatformChange = (value: boolean) => {
    const previousSkipState = isSkipRevenuePlatformSelected;
    setIsSkipRevenuePlatformSelected(value);
    fundingApplicationMutation.mutate(
      { id: fundingApplication.id, isEcommerceUnsupported: value },
      { onError: () => setIsSkipRevenuePlatformSelected(previousSkipState) }
    );
  };

  const navigationContext = useFundingApplicationNavigation({ currentPage: PAGE_TYPE.SELECT_PLATFORMS });

  const onSubmit = () => {
    const validationErrors = validateSelection(
      selectedPlatforms,
      intl,
      fundingApplication,
      fundingApplicationStages,
      isSkipRevenuePlatformSelected
    );
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      // Scroll to top to show the error
      window.scroll({ top: 0, left: 0, behavior: "smooth" });
    } else {
      if (navigationContext.nextPath) {
        navigationContext.navigate(navigationContext.nextPath);
      }
    }
  };

  const eCommerceOptions: MultiConnectionGroupItem[] = filterConnectorsByStageConfig(
    filteredandSortedDataSources.shoppingDataSources,
    fundingApplicationStages.revenue
  )
    // Temporary: Filter out Magento until fully removed from codebase
    .filter((connector) => connector.slug !== "magento2")
    .map((connector) => {
      const isRequired = fundingApplicationStages.revenue?.requiredConnectorSlugs?.includes(connector.slug);
      const isSecondary = isSecondaryRevenuePlatform(connector.slug);
      const isDisabled = !isRequired && isSecondary && (hasRequiredRevenueSlug || hasRevenuePrimarySelected);
      const isSelected = isRequired || selectedPlatforms[connector.slug] || false;

      return getChecklistItemPropsForConnector(
        connector,
        isSelected,
        handlePlatformToggle,
        // Readonly as this is a required slug
        // Could include some helper text so this is not confusing
        isRequired,
        isDisabled
      );
    });

  const marketingOptions = filterConnectorsByStageConfig(
    filteredandSortedDataSources.marketingDataSources,
    fundingApplicationStages.marketing
  ).map((connector) =>
    getChecklistItemPropsForConnector(
      connector,
      fundingApplicationStages.marketing?.requiredConnectorSlugs?.includes(connector.slug) ||
        selectedPlatforms[connector.slug] ||
        false,
      handlePlatformToggle,
      fundingApplicationStages.marketing?.requiredConnectorSlugs?.includes(connector.slug)
    )
  );

  const accountingOptions = filterConnectorsByStageConfig(
    getFilteredAccountingConnectors(filteredandSortedDataSources),
    fundingApplicationStages.accounting
  ).map((connector) =>
    getChecklistItemPropsForConnector(
      connector,
      fundingApplicationStages.accounting?.requiredConnectorSlugs?.includes(connector.slug) ||
        selectedPlatforms[connector.slug] ||
        false,
      handlePlatformToggle,
      fundingApplicationStages.accounting?.requiredConnectorSlugs?.includes(connector.slug)
    )
  );
  const selectedUnsupportedAccounting = [
    ...selectedUnsupportedPlatforms[ACCOUNTING],
    ...(areAccountingDocumentsRequired ? [{ name: NOT_LISTED, checked: true }] : []),
  ].map((platform: UnsupportedPlatformUiState) =>
    getChecklistItemForUnsupportedPlatform(
      platform.name,
      ACCOUNTING,
      platform.checked,
      handleUnsupportedPlatformToggle,
      intl
    )
  );

  return (
    <PageGridColumn $gap="8">
      {errors.length > 0 ? (
        <PageGridRow>
          <StatusBanner variant="critical" title={errors.join(", ")} />
        </PageGridRow>
      ) : null}

      {showRevenue && (
        <ConnectionGroup
          selectionMode="multi"
          isOptional={!fundingApplicationStages.revenue?.platformRequired}
          categoryLabel={intl.formatMessage(MESSAGES.onlineSalesPlatformCategory)}
          subText={intl.formatMessage(MESSAGES.onlineSalesCategoryDescription)}
          options={eCommerceOptions}
          selectedUnsupportedPlatforms={[]}
          unsupportedOptions={[]}
          addUnsupportedPlatform={() => {}}
          skipPlatformProps={{
            onChange: handleSkipRevenuePlatformChange,
            disabled: hasRequiredRevenueSlug || hasRevenuePrimarySelected || hasRevenueSecondarySelected,
            selected: isSkipRevenuePlatformSelected,
          }}
          SkipPlatformSelectionComponent={SkipPlatformSelectionModal}
          helperText={
            showSecondaryDeselectedMessage && selectedRevenuePrimaryPlatformName
              ? intl.formatMessage(MESSAGES.primaryPlatformSelectedHelperText, {
                  platformName: selectedRevenuePrimaryPlatformName,
                })
              : undefined
          }
        />
      )}
      {showAccounting && (
        <ConnectionGroup
          selectionMode="multi"
          isOptional={!fundingApplicationStages.accounting?.platformRequired}
          creatable={false}
          categoryLabel={intl.formatMessage(MESSAGES.accountingPlatformCategory)}
          subText={intl.formatMessage(MESSAGES.accountingCategoryDescription)}
          options={accountingOptions}
          selectedUnsupportedPlatforms={selectedUnsupportedAccounting}
          unsupportedOptions={[NOT_LISTED, ...CODAT_SUPPORTED_ACCOUNTING]}
          addUnsupportedPlatform={(platform: string) =>
            handleUnsupportedPlatformToggle({ category: ACCOUNTING, name: platform, value: true })
          }
          AddPlatformComponent={AddCustomPlatformInput}
        />
      )}
      {showMarketing && (
        <ConnectionGroup
          selectionMode="multi"
          isOptional={!fundingApplicationStages.marketing?.platformRequired}
          categoryLabel={intl.formatMessage(MESSAGES.marketingPlatformCategory)}
          subText={intl.formatMessage(MESSAGES.marketingCategoryDescription)}
          options={marketingOptions}
          selectedUnsupportedPlatforms={[]}
          unsupportedOptions={[]}
          addUnsupportedPlatform={() => {}}
        />
      )}
      <PageGridRow>
        <PageGridCell layout={{ md: { span: 2 } }}>
          <Button
            variant="Primary"
            onClick={onSubmit}
            data-dd-action-name="select-platforms-continue"
            data-testid="navigation-continue-button"
            iconRight={fundingApplicationStages.banking ? IconArrowRight24Line : undefined}
          >
            {fundingApplicationStages.banking
              ? intl.formatMessage(MESSAGES.connectBankAccountsCta)
              : intl.formatMessage(MESSAGES.continueApplicationLabel)}
          </Button>
        </PageGridCell>
      </PageGridRow>
    </PageGridColumn>
  );
};
